import { useCallback, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { EmptyArray } from 'lib/array/arrayUtils'
import noop from 'lib/function/noop'
import type {
  AssistantChatRecord,
  AssistantSurfaceContextSnapshot,
} from 'tripPlanner/assistant/api/types'
import { sortAssistantChatRecordsByLastActivity } from 'tripPlanner/assistant/api/chats'
import { mapAssistantChatTitlePayloadToGenerateChatTitleParams } from 'tripPlanner/assistant/api/chatTitle'
import {
  useChatList,
  useClaimChatsMutation,
  useDeleteChatMutation,
  useGenerateChatTitleMutation,
  useUpdateChatMutation,
  type ChatListResult,
  type GetChatResult,
} from 'tripPlanner/hooks/api/chat'
import * as ChatKeys from 'tripPlanner/hooks/reactQueryKeys/chat'
import { getChatLastActivityTimestamp } from 'tripPlanner/utils/chatTimestamps'

interface UseAssistantHistoryArgs {
  enabled?: boolean
  isLoggedIn: boolean
}

function updateChatListCache(
  previous: ChatListResult | undefined,
  chatIdToUpdate: number,
  updateChat: (chat: AssistantChatRecord) => AssistantChatRecord,
): ChatListResult | undefined {
  if (!previous) {
    return previous
  }

  let didChange = false
  const nextChats = previous.map(chat => {
    if (chat.id !== chatIdToUpdate) {
      return chat
    }

    didChange = true
    return updateChat(chat)
  })

  return didChange ? nextChats : previous
}

export function useAssistantHistory({ enabled = true, isLoggedIn }: UseAssistantHistoryArgs) {
  const queryClient = useQueryClient()
  const shouldLoadHistory = enabled && isLoggedIn
  const {
    data: chatHistoryResponse = EmptyArray,
    isLoading: isChatListLoading,
    refetch: refetchChatList,
  } = useChatList({ enabled: shouldLoadHistory, staleTime: 30_000 })

  const { mutateAsync: deleteChatMutation, isLoading: isDeleteMutationLoading } = useDeleteChatMutation()
  const { mutateAsync: claimChatsMutation } = useClaimChatsMutation()
  const { mutateAsync: generateChatTitleMutation } = useGenerateChatTitleMutation()
  const { mutateAsync: updateChatMutation } = useUpdateChatMutation()

  // TripPlanner owns all assistant surfaces, and chat history is shared across
  // them. Surface changes alter prompt/model/tool context, not history scope.
  const chatHistory: Array<AssistantChatRecord> = useMemo(() => {
    if (!shouldLoadHistory) {
      return EmptyArray
    }
    return sortAssistantChatRecordsByLastActivity(chatHistoryResponse)
  }, [chatHistoryResponse, shouldLoadHistory])

  const isHistoryListLoading = shouldLoadHistory && (isChatListLoading || isDeleteMutationLoading)

  const updateChatListTitle = useCallback((chatIdToUpdate: number, title: string) => {
    queryClient.setQueryData<ChatListResult | undefined>(ChatKeys.list, (previous) => {
      return updateChatListCache(previous, chatIdToUpdate, chat => ({
        ...chat,
        name: title,
      }))
    })
  }, [queryClient])

  const updateChatMetadata = useCallback((chatIdToUpdate: number, name: string, updatedAt: string) => {
    queryClient.setQueryData<ChatListResult | undefined>(ChatKeys.list, (previous) => {
      return updateChatListCache(previous, chatIdToUpdate, chat => ({
        ...chat,
        name,
        updatedAt,
      }))
    })

    queryClient.setQueryData<GetChatResult | undefined>(ChatKeys.detail(chatIdToUpdate), (previous) => {
      if (!previous) {
        return previous
      }
      return {
        ...previous,
        name,
        updatedAt,
      }
    })
  }, [queryClient])

  const updateChatLastActivityTimestamp = useCallback((chatIdToUpdate: number, isoTimestamp: string) => {
    queryClient.setQueryData<ChatListResult | undefined>(ChatKeys.list, (previous) => {
      return updateChatListCache(previous, chatIdToUpdate, chat => {
        if (getChatLastActivityTimestamp(chat) === isoTimestamp) {
          return chat
        }

        return {
          ...chat,
          updatedAt: isoTimestamp,
          lastMessageAt: isoTimestamp,
        }
      })
    })
  }, [queryClient])

  const startChatTitleGeneration = useCallback(async(
    chatIdToUpdate: number,
    message: string,
    surfaceContextSnapshot?: AssistantSurfaceContextSnapshot,
  ) => {
    const trimmedMessage = message.trim()
    if (!trimmedMessage) {
      return { started: false }
    }
    try {
      const response = await generateChatTitleMutation(mapAssistantChatTitlePayloadToGenerateChatTitleParams({
        chatId: chatIdToUpdate,
        message: trimmedMessage,
        surfaceContextSnapshot,
      }))
      updateChatListTitle(chatIdToUpdate, response.title)
    } catch {
      return { started: false }
    }

    if (shouldLoadHistory) {
      refetchChatList().catch(noop)
    }
    return { started: true }
  }, [generateChatTitleMutation, updateChatListTitle, shouldLoadHistory, refetchChatList])

  return useMemo(() => ({
    chatHistory,
    recentChats: chatHistory,
    isHistoryListLoading,
    refetchChatList,
    deleteChatMutation,
    claimChatsMutation,
    generateChatTitleMutation,
    updateChatMutation,
    updateChatListTitle,
    updateChatMetadata,
    updateChatLastActivityTimestamp,
    startChatTitleGeneration,
  }), [
    chatHistory,
    isHistoryListLoading,
    refetchChatList,
    deleteChatMutation,
    claimChatsMutation,
    generateChatTitleMutation,
    updateChatMutation,
    updateChatListTitle,
    updateChatMetadata,
    updateChatLastActivityTimestamp,
    startChatTitleGeneration,
  ])
}
